import { ReadingGenerationModeSelector } from '../Components/ReadingGenerationModeSelector';
import { TEXT_OR_LINK_ID, usePacketInput } from '../Contexts/PacketInputContext';
import { useHomepageModals } from '../Contexts/HomepageModalsContext';
import { useGenerateResources } from '../Hooks/useGenerateResource';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getGoogleDocsPdf, isYoutubeUrl, uploadPdfToS3 } from '../Utils/GenerationUtils';
import { Mixpanel } from '../../../services/Mixpanel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useState } from 'react';
import validator from 'validator';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useError } from '../../../contexts/ErrorContext';

export const isValidUrl = (url) => {
  return validator.isURL(url);
};

export const isValidGoogleDocsUrl = (url) => {
  return url.includes('docs.google.com/presentation') || url.includes('docs.google.com/document/');
};

export const TextOrLinkInput = () => {
  const { inputType, url, pdfFile, setText, setUrl, setPdfFile, setUploadedPdfURL } =
    usePacketInput();
  const { setYoutubeTranscriptModal } = useHomepageModals();
  const { handleGenerateResources } = useGenerateResources();
  const [inputValue, setInputValue] = useState('');
  const { showError } = useError();

  const inputMode = pdfFile ? 'pdf' : url ? 'url' : 'text';
  const handleInputChange = async (value) => {
    setInputValue(value);

    // Clear all inputs first
    setText('');
    setUrl('');
    setPdfFile(null);
    setUploadedPdfURL(null);

    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return;
    }

    // Check if it's a URL
    if (isValidUrl(trimmedValue)) {
      // Intercept YouTube URLs — open transcript modal
      if (isYoutubeUrl(trimmedValue)) {
        setInputValue('');
        Mixpanel.track('YouTube URL Detected', { url: trimmedValue });
        setYoutubeTranscriptModal({
          url: trimmedValue,
          onTranscript: (transcript) => {
            setInputValue(transcript);
            setText(transcript);
          },
        });
        return;
      }

      setUrl(trimmedValue);
      if (isValidGoogleDocsUrl(trimmedValue)) {
        setPdfFile({ name: 'Processing link. This may take a minute', size: 0 });
        const pdfFile = await getGoogleDocsPdf(trimmedValue);
        if (pdfFile) {
          const signedUrl = await uploadPdfToS3(pdfFile);
          setPdfFile(pdfFile);
          setUploadedPdfURL(signedUrl);
        } else {
          showError({
            title: "Can't Access Google Document",
            message: 'Please update the sharing settings to “Anyone with the link can view."',
            type: 'warning',
          });
          setInputValue('');
          setPdfFile(null);
        }
      }
    } else {
      setText(value);
    }
  };

  // Early return after all hooks have been called
  if (inputType.id !== TEXT_OR_LINK_ID) return null;

  const onClearUploadedPdf = () => {
    setPdfFile(null);
    setUrl('');
    setUploadedPdfURL(null);
    setInputValue('');
  };

  return (
    <>
      <div>
        {!pdfFile && (
          <TextInput
            value={inputValue}
            onChange={handleInputChange}
            inputMode={inputMode}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                handleGenerateResources();
              }
            }}
          />
        )}
        {inputMode === 'pdf' && <UploadedPdfPill onClearUploadedPdf={onClearUploadedPdf} />}
      </div>

      <div className='flex gap-x-3 mb-2'>
        <ReadingGenerationModeSelector />
        <UploadButton />
      </div>
    </>
  );
};

const TextInput = ({ value, onChange, onKeyDown, inputMode }) => {
  const characterLimit = 200000;
  const placeholder = `Paste a text, or link to an article`;

  return (
    <>
      <textarea
        autoFocus
        type='textarea'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        maxLength={characterLimit}
        className={`w-full px-2 pt-6 pb-6 border-none ${value && inputMode !== 'url' ? 'resize-y' : 'resize-none'} focus:ring-0 placeholder:text-gray-400 placeholder:font-light caret-emerald-600 scrollbar-minimal`}
      />
      {value.length === characterLimit && (
        <div className='text-xs text-red-600'>You've reached the character limit.</div>
      )}
    </>
  );
};

const UploadedPdfPill = ({ onClearUploadedPdf }) => {
  const { pdfFile, uploadedPdfURL } = usePacketInput();
  const isUploadingPdf = !!pdfFile && !uploadedPdfURL;
  if (!pdfFile) {
    return null;
  }
  return (
    <div className='w-full pt-6 pb-11'>
      <div className='text-base border w-fit border-gray-100 bg-gray-50 text-gray-900 px-3 py-2 rounded-lg flex items-center gap-x-3'>
        {pdfFile.name}
        <button
          disabled={isUploadingPdf}
          onClick={() => {
            Mixpanel.track('PDF Removed');
            onClearUploadedPdf();
          }}>
          {isUploadingPdf ? (
            <LoadingSpinner color='text-gray-400' />
          ) : (
            <XMarkIcon className='w-4 h-4' />
          )}
        </button>
      </div>
    </div>
  );
};

const UploadButton = () => {
  const { setPdfFile, pdfFile, uploadedPdfURL, setUploadedPdfURL } = usePacketInput();
  const isUploadingPdf = !!pdfFile && !uploadedPdfURL;
  const isDisabled = !!pdfFile || isUploadingPdf;

  return (
    <button
      disabled={isDisabled}
      onClick={() => {
        Mixpanel.track('Upload PDF Clicked');
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf';
        fileInput.onchange = async (e) => {
          const selectedFile = e.target.files[0];
          if (!selectedFile) {
            return;
          }

          Mixpanel.track('PDF Uploaded', {
            name: selectedFile.name,
            size: selectedFile.size,
          });

          if (selectedFile.size > 50 * 1000 * 1000) {
            alert('Please choose a smaller file: limit is 50MB');
            return;
          }

          // set the pdf immediately
          setPdfFile(selectedFile);

          setTimeout(async () => {
            // then upload the pdf to s3. doing this in a timeout so that the
            // UI is not blocked.
            const signedUrl = await uploadPdfToS3(selectedFile);
            setUploadedPdfURL(signedUrl);
          });
        };
        fileInput.click();
      }}
      className={`text-xs sm:text-sm px-3 py-2 gap-x-1  w-fit  rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-100 inline-flex justify-start items-center gap-1 ${isDisabled ? 'bg-gray-50 text-gray-400' : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}`}>
      <div className={`${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
        <FileUploadIcon sx={{ fontSize: '1rem' }} />
      </div>
      Upload a PDF
    </button>
  );
};
